'use client'

import { useEffect, useState } from 'react'
import { useTheme, Theme } from '@/components/theme/ThemeProvider'

// ============================================================================
// THEME TOGGLE — cycles Light → Dark → System
// ============================================================================

const SunIcon = (): React.JSX.Element => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 3v2.25m6.364.386l-1.591 1.591M21 12h-2.25m-.386 6.364l-1.591-1.591M12 18.75V21m-4.773-4.227l-1.591 1.591M5.25 12H3m4.227-4.773L5.636 5.636M15.75 12a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0z" />
  </svg>
)

const MoonIcon = (): React.JSX.Element => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M21.752 15.002A9.718 9.718 0 0118 15.75c-5.385 0-9.75-4.365-9.75-9.75 0-1.33.266-2.597.748-3.752A9.753 9.753 0 003 11.25C3 16.635 7.365 21 12.75 21a9.753 9.753 0 009.002-5.998z" />
  </svg>
)

const SystemIcon = (): React.JSX.Element => (
  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.6}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12H3a1.5 1.5 0 00-1.5 1.5v9a1.5 1.5 0 001.5 1.5h18a1.5 1.5 0 001.5-1.5v-9a1.5 1.5 0 00-1.5-1.5z" />
  </svg>
)

const ORDER: Theme[] = ['light', 'dark', 'system']
const LABEL: Record<Theme, string> = {
  light: 'Light',
  dark: 'Dark',
  system: 'System',
}

export function ThemeToggle(): React.JSX.Element {
  const { theme, setTheme } = useTheme()
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  // Reserve space before hydration to avoid layout shift / flash.
  if (!mounted) {
    return <div className="h-10 w-10 rounded-xl" aria-hidden="true" />
  }

  const cycle = (): void => {
    const nextIndex = (ORDER.indexOf(theme) + 1) % ORDER.length
    setTheme(ORDER[nextIndex])
  }

  const icon =
    theme === 'light' ? <SunIcon /> : theme === 'dark' ? <MoonIcon /> : <SystemIcon />

  return (
    <button
      type="button"
      onClick={cycle}
      title={`Theme: ${LABEL[theme]} (click to change)`}
      aria-label={`Switch theme. Current: ${LABEL[theme]}`}
      className="group inline-flex h-10 w-10 items-center justify-center rounded-xl border border-border bg-surface/70 text-foreground/80 shadow-sm backdrop-blur transition-colors hover:border-nova-400/60 hover:text-nova-500 focus-visible:ring-2 focus-visible:ring-nova-400"
    >
      <span className="transition-transform duration-300 group-hover:rotate-12">{icon}</span>
    </button>
  )
}
